import Axios from 'axios';
import Cookies from 'js-cookie';

import { instance } from '@/apis/core';

import { OrderLineWithId } from '../orders/types';

import { Cart } from './types';

const { REACT_APP_LINK_API_ENDPOINT, REACT_APP_LINK_API_PROTOCOL, REACT_APP_LINK_API_PORT } =
  process.env;
const BASE_URL = `${REACT_APP_LINK_API_PROTOCOL}://${REACT_APP_LINK_API_ENDPOINT}:${REACT_APP_LINK_API_PORT}`;

const axiosWithoutAuth = Axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
});

const requestInstance = async <T>(
  request: (axiosInstance: typeof instance) => Promise<T>,
): Promise<T> => {
  const isAuth = !!Cookies.get('customer_token');
  const axiosInstance = isAuth ? instance : axiosWithoutAuth;
  return request(axiosInstance);
};

export interface PostCartsOrderlinePayload extends OrderLineWithId {
  campaign_id: number;
}

interface CartRepositoryImpl {
  getCarts(cart_group_ids?: number[]): Promise<Cart>;
  postCarts(market_id: number, orderlines: PostCartsOrderlinePayload[]): Promise<void>;
  deleteCarts(): Promise<void>;
  deleteCartGroup(cart_group_id: number[]): Promise<void>;
  deleteCartItem(cart_item_id: number): Promise<void>;
  patchCartItem(cart_item_id: number, quantity: number): Promise<void>;
  checkOtherCart(market_id: number): Promise<{ has_other_cart: boolean }>;
  getCartCount(): Promise<{ cart_count: number }>;
}

class CartRepository implements CartRepositoryImpl {
  // 장바구니 조회
  getCarts = async (cart_group_ids?: number[]): Promise<Cart> => {
    return requestInstance(async (axiosInstance) => {
      const { data } = await axiosInstance.get('/carts', {
        params: {
          cart_group_ids,
        },
      });

      return data;
    });
  };

  // 장바구니 추가
  postCarts = async (market_id: number, orderlines: PostCartsOrderlinePayload[]) => {
    return requestInstance(async (axiosInstance) => {
      await axiosInstance.post('/carts', {
        market_id,
        orderlines,
      });
    });
  };

  // 장바구니 전체 삭제
  deleteCarts = async () => {
    return requestInstance(async (axiosInstance) => {
      await axiosInstance.delete('/carts');
    });
  };

  // 장바구니 그룹 삭제
  deleteCartGroup = async (cart_group_ids: number[]) => {
    return requestInstance(async (axiosInstance) => {
      await axiosInstance.delete('/cart-groups', {
        data: { cart_group_ids },
      });
    });
  };

  // 장바구니 상품 삭제
  deleteCartItem = async (cart_item_id: number) => {
    return requestInstance(async (axiosInstance) => {
      await axiosInstance.delete(`/cart-items/${cart_item_id}`);
    });
  };

  // 장바구니 상품 수량 변경
  patchCartItem = async (cart_item_id: number, quantity: number) => {
    return requestInstance(async (axiosInstance) => {
      await axiosInstance.patch(`/cart-items/${cart_item_id}`, { quantity });
    });
  };

  // 특정 마켓 외 장바구니 존재 여부 확인
  checkOtherCart = async (market_id: number) => {
    return requestInstance(async (axiosInstance) => {
      const { data } = await axiosInstance.get(`/market/${market_id}/has-other-carts`);

      return data;
    });
  };

  // 장바구니 수 조회
  getCartCount = async (): Promise<{ cart_count: number }> => {
    return requestInstance(async (axiosInstance) => {
      const { data } = await axiosInstance.get('/carts/count');

      return data;
    });
  };
}

export const cart = new CartRepository();
